'use client';

import { useMemo } from 'react';

import {
  CreateDonationRequestModeEnum,
  Project,
  ProjectBeneficiaryTypeEnum,
  ProjectSettingsModeGroupEnum,
  Reward,
  Subproject,
} from '@/app/api';

import { DonationFormType } from '@/app/enums';
import { useDonationAmountOptions } from '@/app/hooks/useDonationAmountOptions ';
import { useInactiveTabTitleFlash } from '@/app/hooks/useInactiveTabTitleFlash';
import { getCountryConfig } from '@/app/utils/countryConfig';
import { useTranslation } from 'react-i18next';

import DonationForm from '@/app/components/DonationForm/DonationForm';
import { useCurrentUser } from '@/app/hooks/useCurrentUser';
import { useSearchParams } from 'next/navigation';
import { isStringParsableToNumber } from '@/app/utils/numbers';
import { getDefaultAmountBasedOnGroup } from '@/app/utils/abTesting/checkout-form';
import { useAbGroup } from '@/app/providers/ConfigProvider';

const countryConfig = getCountryConfig();

const isPrivacyConsentCheckboxSupported = countryConfig.donationFormCommon.isPrivacyConsentCheckboxSupported;

interface DonationDetailProps {
  project: Project;
  subproject?: Subproject;
  projectBeneficiaryType: ProjectBeneficiaryTypeEnum | null;
  rewards?: Reward[];
}

const DonationDetail = ({ project, subproject, projectBeneficiaryType, rewards }: DonationDetailProps) => {
  const { t } = useTranslation();
  const { currentUser } = useCurrentUser();

  const abGroup = useAbGroup();

  const amountOptions = useDonationAmountOptions(project.settings.donationAmounts);
  const searchParams = useSearchParams();

  const amountSearchParam = searchParams.get('amount');

  const initialValues = {
    amount:
      amountSearchParam && isStringParsableToNumber(amountSearchParam)
        ? Number(amountSearchParam)
        : getDefaultAmountBasedOnGroup(abGroup, project.settings.donationAmounts),

    donationMode:
      searchParams.get('mode') === 'long-term'
        ? CreateDonationRequestModeEnum.LongTerm
        : CreateDonationRequestModeEnum.Single,
    agreedToShareContact:
      projectBeneficiaryType === ProjectBeneficiaryTypeEnum.Organization &&
      countryConfig.organizationDefaultAgreedToShareContact,
  };

  useInactiveTabTitleFlash({
    originalTitle: `${project.content.currentName} | ${t('appName')}`,
    inactiveTitle: t('pages.donationForm.finishTheDonation'),
  });

  const shouldDisplayPrivacyConsentCheckbox =
    projectBeneficiaryType === ProjectBeneficiaryTypeEnum.Organization &&
    project.settings.modeGroup === ProjectSettingsModeGroupEnum.Dobro &&
    isPrivacyConsentCheckboxSupported;

  const formType = useMemo(() => {
    if (project.settings.modeGroup === ProjectSettingsModeGroupEnum.Plus) {
      return DonationFormType.COMMERCIAL_PROJECT;
    }

    if (project.settings.isGoodHabit) {
      if (currentUser) {
        return DonationFormType.GOOD_HABIT_AUTHENTICATED_USER;
      }

      return DonationFormType.GOOD_HABIT_UNAUTHENTICATED_USER;
    }

    if (!project.settings.isLongTerm) {
      if (currentUser) {
        return DonationFormType.SINGLE_AUTHENTICATED_USER;
      }

      return DonationFormType.SINGLE_UNAUTHENTICATED_USER;
    }

    if (project.settings.isLongTerm) {
      if (currentUser) {
        return DonationFormType.LONG_TERM_AUTHENTICATED_USER;
      }

      return DonationFormType.LONG_TERM_UNAUTHENTICATED_USER;
    }

    return DonationFormType.SINGLE_UNAUTHENTICATED_USER;
  }, [project, currentUser]);

  switch (formType) {
    case DonationFormType.LONG_TERM_AUTHENTICATED_USER:
      return (
        <div className="w-full min-h-[100vh] rounded md:pb-[40px]">
          <DonationForm
            amountOptions={amountOptions}
            project={project}
            subproject={subproject}
            formType={formType}
            initialValues={initialValues}
            shouldDisplayPrivacyConsentCheckbox={shouldDisplayPrivacyConsentCheckbox}
          />
        </div>
      );

    case DonationFormType.LONG_TERM_UNAUTHENTICATED_USER:
      return (
        <div className="w-full min-h-[100vh] rounded md:pb-[40px]">
          <DonationForm
            amountOptions={amountOptions}
            project={project}
            subproject={subproject}
            formType={formType}
            initialValues={initialValues}
            shouldDisplayPrivacyConsentCheckbox={shouldDisplayPrivacyConsentCheckbox}
          />
        </div>
      );

    case DonationFormType.GOOD_HABIT_AUTHENTICATED_USER:
      return (
        <div className="w-full min-h-[100vh] rounded md:pb-[40px]">
          <DonationForm
            amountOptions={amountOptions}
            project={project}
            subproject={subproject}
            formType={formType}
            initialValues={initialValues}
            shouldDisplayPrivacyConsentCheckbox={shouldDisplayPrivacyConsentCheckbox}
          />
        </div>
      );

    case DonationFormType.GOOD_HABIT_UNAUTHENTICATED_USER:
      return (
        <div className="w-full min-h-[100vh] rounded md:pb-[40px]">
          <DonationForm
            amountOptions={amountOptions}
            project={project}
            subproject={subproject}
            formType={formType}
            initialValues={initialValues}
            shouldDisplayPrivacyConsentCheckbox={shouldDisplayPrivacyConsentCheckbox}
          />
        </div>
      );

    case DonationFormType.COMMERCIAL_PROJECT:
      return (
        <div className="w-full min-h-[100vh] rounded md:pb-[40px]">
          <DonationForm
            rewards={rewards}
            project={project}
            subproject={subproject}
            formType={formType}
            initialValues={initialValues}
            shouldDisplayPrivacyConsentCheckbox={shouldDisplayPrivacyConsentCheckbox}
          />
        </div>
      );

    case DonationFormType.SINGLE_AUTHENTICATED_USER:
      return (
        <div className="w-full min-h-[100vh] rounded md:pb-[40px]">
          <DonationForm
            amountOptions={amountOptions}
            project={project}
            subproject={subproject}
            formType={formType}
            initialValues={initialValues}
            shouldDisplayPrivacyConsentCheckbox={shouldDisplayPrivacyConsentCheckbox}
          />
        </div>
      );

    case DonationFormType.SINGLE_UNAUTHENTICATED_USER:
      return (
        <div className="w-full min-h-[100vh] rounded md:pb-[40px]">
          <DonationForm
            amountOptions={amountOptions}
            project={project}
            subproject={subproject}
            formType={formType}
            initialValues={initialValues}
            shouldDisplayPrivacyConsentCheckbox={shouldDisplayPrivacyConsentCheckbox}
          />
        </div>
      );

    default:
      return null;
  }
};

export default DonationDetail;
